import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, User, LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import { LOGO_DARK_BG, LOGO_LIGHT_BG, CONTACT } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/properties", label: "Properties" },
    { to: "/services", label: "Services" },
    { to: "/insights", label: "Insights" },
    { to: "/about", label: "About", children: [{ to: "/about", label: "About EBG Realty" }, { to: "/about/media-coverage", label: "Media Coverage" }] },
    { to: "/contact", label: "Contact" },
];

export default function Nav({ variant = "auto" }) {
    // variant: 'auto' (dark on top when at top, light after scroll), 'dark' (transparent over hero), 'light' (solid ivory)
    const location = useLocation();
    const isHome = location.pathname === "/";
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { user, logout } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    const isDark = !scrolled && (variant === "dark" || (variant === "auto" && isHome));
    const barBg = isDark
        ? "bg-gradient-to-b from-[#0a0d18]/92 via-[#0a0d18]/70 to-[#0a0d18]/25"
        : "bg-[var(--ebg-ivory)] border-b border-[var(--ebg-border-light)] shadow-[0_2px_20px_-8px_rgba(0,0,0,0.18)]";
    const linkColor = isDark ? "text-white/90 hover:text-white" : "text-black/80 hover:text-black";
    const logoSrc = isDark ? LOGO_DARK_BG : LOGO_LIGHT_BG;

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${barBg}`}
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
                    <img src={logoSrc} alt="EBG Realty" className="h-12 md:h-14 w-auto" />
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {LINKS.map((l) => l.children ? (
                        <div key={l.to} className="relative group flex items-center" data-testid={`nav-group-${l.label.toLowerCase()}`}>
                            <NavLink to={l.to} data-testid={`nav-link-${l.label.toLowerCase()}`}
                                className={({ isActive }) => `text-[0.72rem] uppercase tracking-[0.24em] font-bold transition-colors ${linkColor} ${isActive ? "!text-[var(--ebg-gold)]" : ""}`}>
                                {l.label}
                            </NavLink>
                            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity">
                                <div className="bg-[#0a0d18] border border-white/10 min-w-[200px] py-2 shadow-xl">
                                    {l.children.map((c) => (
                                        <Link key={c.to} to={c.to} data-testid={`nav-sub-${c.to.split("/").pop()}`} className="block px-5 py-2.5 text-[0.68rem] uppercase tracking-[0.2em] font-bold text-white/80 hover:text-[var(--ebg-gold)] hover:bg-white/5">{c.label}</Link>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-[0.72rem] uppercase tracking-[0.24em] font-bold transition-colors ${linkColor} ${isActive ? "!text-[var(--ebg-gold)]" : ""}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-5">
                    <a
                        href={CONTACT.tel}
                        data-testid="nav-phone"
                        className={`text-[13px] tabular-nums font-medium inline-flex items-center gap-2 transition-colors ${
                            isDark ? "text-white/85 hover:text-[var(--ebg-gold)]" : "text-black/80 hover:text-[var(--ebg-gold)]"
                        }`}
                    >
                        <span className={`w-7 h-7 rounded-full border flex items-center justify-center ${
                            isDark ? "border-[var(--ebg-gold-line)] text-[var(--ebg-gold)]" : "border-black/20 text-black"
                        }`}>
                            <Phone size={12} />
                        </span>
                        {CONTACT.display}
                    </a>
                    <span className={`w-px h-4 ${isDark ? "bg-white/25" : "bg-black/20"}`} />
                    <Link
                        to="/contact"
                        data-testid="nav-cta-consult"
                        className={`text-[11px] uppercase tracking-[0.28em] font-bold px-5 py-3 transition-all ${
                            isDark
                                ? "bg-[var(--ebg-gold)] text-black hover:bg-white"
                                : "bg-black text-[var(--ebg-ivory)] hover:bg-[var(--ebg-gold)] hover:text-black"
                        }`}
                    >
                        Book Consultation
                    </Link>
                    <UserMenu isDark={isDark}/>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    className={`lg:hidden ${isDark ? "text-white" : "text-black"}`}
                    onClick={() => setMobileOpen((s) => !s)}
                    aria-label="Toggle menu"
                >
                    {mobileOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {mobileOpen && (
                <div
                    data-testid="nav-mobile-panel"
                    className="lg:hidden bg-[var(--ebg-black)] text-[var(--ebg-ivory)] border-t border-[var(--ebg-border-dark)]"
                >
                    <div className="px-6 py-8 flex flex-col gap-5">
                        {LINKS.map((l) => (<React.Fragment key={l.to}>
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `overline text-sm ${isActive ? "text-[var(--ebg-gold)]" : "text-white/85"}`
                                }
                            >
                                {l.label}
                            </NavLink>
                            {l.children && <Link to="/about/media-coverage" data-testid="nav-mobile-link-media-coverage" className="overline text-sm text-white/60 pl-4">— Media Coverage</Link>}
                        </React.Fragment>))}
                        <Link
                            to="/contact"
                            data-testid="nav-mobile-cta"
                            className="text-[11px] uppercase tracking-[0.28em] font-bold bg-[var(--ebg-gold)] text-black px-5 py-3 text-center mt-3"
                        >
                            Book Consultation
                        </Link>
                        <a
                            href={CONTACT.tel}
                            data-testid="nav-mobile-phone"
                            className="text-sm text-white/80 mt-2 text-center tabular-nums"
                        >
                            {CONTACT.display}
                        </a>

                        {/* ---------- Auth block ---------- */}
                        <div className="mt-5 pt-5 border-t border-white/10" data-testid="nav-mobile-auth">
                            {user ? (
                                <div className="space-y-3">
                                    {/* User card */}
                                    <div className="flex items-center gap-3">
                                        <span className="w-10 h-10 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center text-[13px] font-bold">
                                            {(user.name || user.email)[0].toUpperCase()}
                                        </span>
                                        <div className="min-w-0">
                                            <div className="font-bold text-[14px] truncate">{user.name}</div>
                                            <div className="text-white/50 text-[11px] truncate">{user.email}</div>
                                            <div className="mt-1 inline-block text-[9px] uppercase tracking-[0.24em] font-bold bg-[var(--ebg-gold)]/15 text-[var(--ebg-gold)] px-2 py-0.5">{user.role}</div>
                                        </div>
                                    </div>
                                    <Link
                                        to={user.role === "admin" ? "/admin/dashboard" : `/portal/${user.role}`}
                                        data-testid="nav-mobile-dashboard"
                                        className="w-full flex items-center justify-center gap-2 border border-white/25 text-white py-3 text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-white/10"
                                    >
                                        <LayoutDashboard size={14} className="text-[var(--ebg-gold)]"/> Go to Dashboard
                                    </Link>
                                    <button
                                        onClick={async () => { await logout(); setMobileOpen(false); nav("/"); }}
                                        data-testid="nav-mobile-logout"
                                        className="w-full flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold text-white/70 py-2 hover:text-white"
                                    >
                                        <LogOut size={13} className="text-red-500"/> Sign Out
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    <Link
                                        to="/login"
                                        data-testid="nav-mobile-signin"
                                        className="border border-white/25 text-white py-3.5 text-center text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-white/10 flex items-center justify-center gap-2"
                                    >
                                        <User size={13}/> Sign In
                                    </Link>
                                    <Link
                                        to="/register"
                                        data-testid="nav-mobile-register"
                                        className="bg-white text-black py-3.5 text-center text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-[var(--ebg-gold)]"
                                    >
                                        Create Account
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}

function UserMenu({ isDark }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);
    if (user === undefined) return null;
    if (!user) {
        return (
            <Link
                to="/login"
                data-testid="nav-signin"
                className={`text-[11px] uppercase tracking-[0.24em] font-bold transition-colors ${isDark ? "text-white/80 hover:text-[var(--ebg-gold)]" : "text-black/70 hover:text-black"}`}
            >
                Sign In
            </Link>
        );
    }
    const dashPath = user.role === "admin" ? "/admin/dashboard" : `/portal/${user.role}`;
    const doLogout = async () => { await logout(); setOpen(false); nav("/"); };
    return (
        <div className="relative" ref={ref} data-testid="user-menu">
            <button
                onClick={() => setOpen((s) => !s)}
                data-testid="user-menu-toggle"
                className={`flex items-center gap-2 px-3 py-2 border transition-colors ${isDark ? "border-white/20 text-white hover:border-[var(--ebg-gold)]" : "border-black/15 text-black hover:border-black"}`}
            >
                <span className="w-7 h-7 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center text-[11px] font-bold">
                    {(user.name || user.email)[0].toUpperCase()}
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] font-bold max-w-[100px] truncate">{user.name?.split(" ")[0] || "Account"}</span>
                <ChevronDown size={12}/>
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-black/10 shadow-xl z-50 text-black" data-testid="user-menu-dropdown">
                    <div className="p-4 border-b border-black/10">
                        <div className="font-bold text-[14px]">{user.name}</div>
                        <div className="text-black/55 text-[12px]">{user.email}</div>
                        <div className="mt-2 inline-block text-[9px] uppercase tracking-[0.24em] font-bold bg-[var(--ebg-gold)]/10 text-[var(--ebg-gold)] px-2 py-0.5">{user.role}</div>
                    </div>
                    <Link to={dashPath} onClick={() => setOpen(false)} data-testid="user-menu-dashboard" className="flex items-center gap-3 px-4 py-3 text-[13px] hover:bg-black/5">
                        <LayoutDashboard size={14} className="text-[var(--ebg-gold)]"/> Go to Dashboard
                    </Link>
                    <button onClick={doLogout} data-testid="user-menu-logout" className="w-full flex items-center gap-3 px-4 py-3 text-[13px] hover:bg-black/5 text-left border-t border-black/10">
                        <LogOut size={14} className="text-red-500"/> Sign Out
                    </button>
                </div>
            )}
        </div>
    );
}
